import { LevelDataMap } from '../performance-data/data-filter-options.pd.model';
import {
    CriteriaIdType,
    MetricData,
    MetricDataItem,
    NO_DATA_ID,
    PerformanceDataViewType,
} from '../performance-data/metric.pd.model';

import {
    ConfigPath,
    ConfigPathLevel,
    ConfigViewMetric,
    ConfigViewPillar,
    DataKey,
    findLevel,
    HierarchyUserInfoItem,
    Metric,
    padMissingMetricsWithMetrics,
} from './config.insight.model';
import {
    Cell,
    CellNode,
    Column,
    DivRenderer,
    flattenArrayWithObj,
    GridNode,
    Row,
    RowNode,
    Sort,
    SortOrder,
} from './experimental.grid';
import { Tab } from './experimental.tabset';
import { TranslationKeyMap } from './insight.i18n';
import {
    OptionalColumn,
    OptionalColumnType,
} from './optional-column.insight.model';
import { TabContext } from './tabset.model';

export interface GridInsightNewTabClickEvent {
    tab: Tab<TabContext<any>>;
}

export interface GridInsightSelectionItem {
    rowId: string;
    levelsDataMap: LevelDataMap[];
    levelsDataMapRow: LevelDataMap[];
    name: string;
}

export interface GridInsightSelectionChangeEvent {
    selected: GridInsightSelectionItem[];
}

export enum ColumnID {
    NODE = 9000,
}

// this must extend OptionalColumnType enum
export enum ColumnType {
    // OptionalColumnType
    CURRENT_ACTUAL = OptionalColumnType.CURRENT_ACTUAL,
    PROJECTED_RESULT = OptionalColumnType.PROJECTED_RESULT,
    PERIOD_TARGET = OptionalColumnType.PERIOD_TARGET,
    GAP = OptionalColumnType.GAP,
    PROJECTED_PERCENT_TO_TARGET = OptionalColumnType.PROJECTED_PERCENT_TO_TARGET,
    PREVIOUS_ACTUAL = OptionalColumnType.PREVIOUS_ACTUAL,
    CURRENT_TARGET = OptionalColumnType.CURRENT_TARGET,
    GOAL = OptionalColumnType.GOAL,
    PERCENT_TO_TARGET = OptionalColumnType.PERCENT_TO_TARGET,
    PEER_GROUP_ACTUAL = OptionalColumnType.PEER_GROUP_ACTUAL,
    PEER_GROUP_PERCENT_TO_TARGET = OptionalColumnType.PEER_GROUP_PERCENT_TO_TARGET,
    OBJECTIVE_MET = OptionalColumnType.OBJECTIVE_MET,
    WEIGHT = OptionalColumnType.WEIGHT,
    WEIGHTED_SCORE = OptionalColumnType.WEIGHTED_SCORE,

    // internal
    NODE = 9000,
    PILLAR,
    SUB_PILLAR,
    METRIC,
    COMPONENT_METRIC,
}

export interface Refs {
    translate: any;
    cmpsNumber: any;
    cmpsPercent: any;
    onExpandPillarClick?: any;
    onExpandMetricClick?: any;
    onExpandRowClick?: any;
    onSortClick?: any;
    onNewTabClick?: any;
    onToggleSelectionItemClick?: any;
}

export interface ColumnContext {
    tpl?: any;
    type: ColumnType;
    name?: string;
    description?: string;
    parentId?: string; // on ColumnType.COMPONENT_METRIC
    hasPillarHandle?: boolean; // on ColumnType.METRIC
    hasComponents?: boolean; // on ColumnType.METRIC
    prevPillarExpanded?: boolean; // on ColumnType.SUB_PILLAR
    pillarExpanded?: boolean; // on ColumnType.SUB_PILLAR | ColumnType.METRIC
    metricExpanded?: boolean; // on ColumnType.METRIC | ColumnType.COMPONENT_METRIC
    autoSized?: boolean;
}

export interface RowContext {
    levelDataMap: LevelDataMap;
    levelDataMapRole: LevelDataMap[];
    depth: number;
    hasNextLevel?: boolean;
    allowNewTab?: boolean;
    autoExpand?: boolean;
    pending?: boolean;
    cancelled?: boolean;
    expanded?: boolean;
    selected?: boolean;
    hasSuperGroup?: boolean;
    hasQueue?: boolean;
}

export interface CellContext<T> {
    tpl?: any;
    value: T;
}

export interface NodeInfo {
    name: string;
    unSelectable?: boolean;
    notCurrentManager?: boolean;
}

export interface MetricPair {
    metric: MetricDataItem;
    peerGroup?: MetricDataItem;
}

export const RowId = {
    create(
        levelIdentifier: string,
        levelIdentifierPath: string,
        depth: number,
        index: number,
        dataId: number,
        parentDataId: number,
        parentLevelDataIdPath: string,
    ): string {
        return `${levelIdentifier}<${levelIdentifierPath}<${depth}<${index}<${dataId}<${parentDataId}<${parentLevelDataIdPath}`;
    },
    parse(rowId: string): {
        levelIdentifier: string;
        levelIdentifierPath: string;
        depth: number;
        index: number;
        dataId: number;
        parentDataId: number;
        parentLevelDataIdPath: string;
    } {
        const parts = rowId.split('<');
        return {
            levelIdentifier: parts[0],
            levelIdentifierPath: parts[1],
            depth: parseInt(parts[2]),
            index: parseInt(parts[3]),
            dataId: parseInt(parts[4]),
            parentDataId: parseInt(parts[5]),
            parentLevelDataIdPath: parts[6],
        };
    },
};

export function concatLevelDataMap(
    row: RowNode<RowContext>,
    limit: number = -1,
): LevelDataMap[] {
    const map: LevelDataMap[] = [];
    let parent: GridNode | RowNode = row;
    while (parent || limit > -1) {
        if (parent instanceof GridNode) {
            map.push(...parent.context.ctx.levelsDataMap);
        } else {
            map.push(parent.context.ctx.levelDataMap);
            if (parent.context.ctx.levelDataMapRole) {
                map.push(...parent.context.ctx.levelDataMapRole);
            }
        }
        parent = parent.parent;
        limit -= 1;
    }
    return map;
}

type RowDefinition = Row<RowContext, CellContext<NodeInfo | MetricPair>>;

// eslint-disable-next-line @typescript-eslint/naming-convention
const ColumnType_METRIC_Height = 50;
// eslint-disable-next-line @typescript-eslint/naming-convention
const ColumnType_NODE_Width = 150;
// eslint-disable-next-line @typescript-eslint/naming-convention
const ColumnType_NODE_Height = 40;

export class DataSource {
    columnTpl: any; // TemplateRef
    nodeTpl: any; // TemplateRef
    metricTpl: any; // TemplateRef
    path: ConfigPath;
    data: DataKey[];
    peerGroupData: DataKey[];
    hierarchyUserInfo: HierarchyUserInfoItem[];
    hierarchyUserInfoCurrent: HierarchyUserInfoItem[];
    columns: Array<Column<ColumnContext>>;
    columnsFlatten: Array<Column<ColumnContext>>;
    metrics: Metric[];
    didProcessInputCriteriaIdTypes: (
        levels: ConfigPathLevel[],
        parentLevelsDataMap: LevelDataMap[],
        levelsDataMap: LevelDataMap[],
    ) => void;

    didMakeRow: (
        level: ConfigPathLevel,
        userInfo: HierarchyUserInfoItem,
        levelDataMapRole: LevelDataMap[],
    ) => void;

    applyHiddenColumns(
        pillarSelection: ConfigViewPillar[],
        columnSelection: OptionalColumn[],
        renderer?: DivRenderer,
    ): [Array<Column<ColumnContext>>, Array<Column<ColumnContext>>] {
        const lookup = this.columnsFlatten.reduce((p, c) => {
            p[c.id] = c.hidden;
            c.hidden = false; // reset
            return p;
        }, []);

        const unchanged: Array<Column<ColumnContext>> = [];
        const changed: Array<Column<ColumnContext>> = [];

        this.columnsFlatten
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            .filter(
                (c) =>
                    (c.ctx.type === ColumnType.PILLAR ||
                        c.ctx.type === ColumnType.SUB_PILLAR) &&
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    c.id.indexOf('-STUB-') === -1,
            )
            .forEach((c) => {
                c.hidden =
                    pillarSelection.findIndex(
                        (ps) => ps.name === c.ctx.name,
                    ) === -1;
                if (lookup[c.id] !== c.hidden) {
                    lookup[c.id] = c.hidden;
                    changed.push(c);
                } else {
                    unchanged.push(c);
                }
                c.children &&
                    flattenArrayWithObj(c.children, 'children')
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        .filter(
                            (cc) =>
                                // eslint-disable-next-line @typescript-eslint/prefer-includes
                                [
                                    ColumnType.METRIC,
                                    ColumnType.COMPONENT_METRIC,
                                ].indexOf(cc.ctx.type) !== -1,
                        )
                        .forEach((cc) => {
                            cc.hidden = c.hidden;
                            if (lookup[cc.id] !== cc.hidden) {
                                lookup[cc.id] = cc.hidden;
                                changed.push(cc);
                            } else {
                                unchanged.push(cc);
                            }
                        });
            });

        this.columnsFlatten
            .filter(
                (c) =>
                    (c.ctx.type === ColumnType.PILLAR ||
                        c.ctx.type === ColumnType.SUB_PILLAR) &&
                    c.children &&
                    c.children.length !== 0,
            )
            .forEach((c) => {
                const pillarExpanded = c.children.some(
                    (cc) =>
                        (cc.ctx.type !== ColumnType.METRIC ||
                            cc.ctx.hasPillarHandle) &&
                        cc.ctx.pillarExpanded,
                );
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                const row =
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    renderer &&
                    renderer.contentColumns.columns.find((cc) =>
                        cc.cells.children.some(
                            (ccc) => ccc.def.columnId === c.id,
                        ),
                    );
                if (row) {
                    row.def.hidden = !pillarExpanded;
                }
            });

        this.columnsFlatten
            .filter(
                (c) =>
                    c.ctx.type === ColumnType.METRIC ||
                    c.ctx.type === ColumnType.COMPONENT_METRIC,
            )
            .forEach((c) => {
                c.hidden =
                    c.hidden ||
                    (c.ctx.type === ColumnType.COMPONENT_METRIC
                        ? !c.ctx.metricExpanded
                        : false);
                if (lookup[c.id] !== c.hidden) {
                    lookup[c.id] = c.hidden;
                    changed.push(c);
                } else {
                    unchanged.push(c);
                }
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                c.children &&
                    c.children.forEach((cc) => {
                        cc.hidden =
                            c.hidden ||
                            columnSelection.findIndex(
                                (sc) =>
                                    sc.id ===
                                    (cc.ctx.type as any as OptionalColumnType),
                            ) === -1;
                        if (lookup[cc.id] !== cc.hidden) {
                            lookup[cc.id] = cc.hidden;
                            changed.push(cc);
                        } else {
                            unchanged.push(cc);
                        }
                    });
            });

        return [unchanged, changed];
    }

    applyExpandedColumnState(state: {
        columnId: string;
        pillarExpanded: boolean;
        metricExpanded: boolean;
    }): [Array<Column<ColumnContext>>, Array<Column<ColumnContext>>] {
        const col = this.columnsFlatten.find((e) => e.id === state.columnId);

        let expandedPillars;
        if (
            state.pillarExpanded != null &&
            (col.ctx.type === ColumnType.SUB_PILLAR ||
                col.ctx.type === ColumnType.METRIC)
        ) {
            if (col.ctx.type === ColumnType.METRIC) {
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                const subPillarParent = this.columnsFlatten.find(
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    (c) => c.children && c.children.indexOf(col) !== -1,
                );
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                const pillarParent =
                    subPillarParent &&
                    this.columnsFlatten.find(
                        (c) =>
                            c.children &&
                            // eslint-disable-next-line @typescript-eslint/prefer-includes
                            c.children.indexOf(subPillarParent) !== -1,
                    );
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, @typescript-eslint/prefer-includes
                const doubleExpand =
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    pillarParent &&
                    pillarParent.children.every(
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        (c) => c.id.indexOf('-STUB-') !== -1,
                    );
                if (doubleExpand) {
                    const singleExpand =
                        state.pillarExpanded &&
                        this.columnsFlatten
                            .filter((c) => c.ctx.type === ColumnType.SUB_PILLAR)
                            .every((c) => c.ctx.prevPillarExpanded === false);
                    expandedPillars = this.columnsFlatten.filter((c) => {
                        if (!singleExpand) {
                            return (
                                (c.ctx.type === ColumnType.SUB_PILLAR ||
                                    c.ctx.type === ColumnType.METRIC) &&
                                (c.ctx.type !== ColumnType.METRIC ||
                                    c.ctx.hasPillarHandle)
                            );
                        } else {
                            return (
                                c.ctx.type === ColumnType.METRIC &&
                                c.ctx.hasPillarHandle
                            );
                        }
                    });
                }
            }
            if (!expandedPillars) {
                expandedPillars = this.columnsFlatten.filter(
                    (c) =>
                        c.ctx.type === col.ctx.type &&
                        (col.ctx.type !== ColumnType.METRIC ||
                            c.ctx.hasPillarHandle),
                );
            }
            expandedPillars.forEach((c) => {
                if (c.ctx.type === ColumnType.SUB_PILLAR) {
                    c.ctx.prevPillarExpanded = c.ctx.pillarExpanded;
                }
                c.ctx.pillarExpanded = state.pillarExpanded;
            });
        }

        let expandedMetrics;
        if (
            state.metricExpanded != null &&
            col.ctx.type === ColumnType.METRIC
        ) {
            this.columnsFlatten
                .filter((c) => c.ctx.metricExpanded)
                // eslint-disable-next-line no-return-assign
                .forEach((c) => (c.ctx.metricExpanded = false)); // reset

            expandedMetrics = this.columnsFlatten.filter(
                (c) =>
                    c.id === state.columnId ||
                    c.ctx.parentId === state.columnId,
            );

            // eslint-disable-next-line no-return-assign
            expandedMetrics.forEach(
                (c) => (c.ctx.metricExpanded = state.metricExpanded),
            );
        }

        return [expandedPillars, expandedMetrics];
    }

    collapseAllPillars(): void {
        this.columnsFlatten
            .filter(
                (c) =>
                    (c.ctx.type === ColumnType.PILLAR ||
                        c.ctx.type === ColumnType.SUB_PILLAR) &&
                    c.children &&
                    c.children.length !== 0,
            )
            .forEach((c) =>
                c.children.forEach((cc) => {
                    if (cc.ctx.type === ColumnType.SUB_PILLAR) {
                        // cc.ctx.prevPillarExpanded = cc.ctx.pillarExpanded;
                        cc.ctx.prevPillarExpanded = false;
                    }
                    cc.ctx.pillarExpanded = undefined;
                }),
            );
        this.columnsFlatten
            .filter(
                (c) =>
                    (c.ctx.type === ColumnType.SUB_PILLAR ||
                        c.ctx.type === ColumnType.METRIC) &&
                    (c.ctx.type !== ColumnType.METRIC || c.ctx.hasPillarHandle),
            )
            // eslint-disable-next-line no-return-assign
            .forEach((c) => (c.ctx.pillarExpanded = false));
    }

    reassignPillarHandles(): void {
        this.columnsFlatten
            .filter(
                (c) =>
                    (c.ctx.type === ColumnType.PILLAR ||
                        c.ctx.type === ColumnType.SUB_PILLAR) &&
                    c.children &&
                    c.children.length !== 0,
            )
            .forEach((c) => {
                const pillarExpanded = c.children.some(
                    (cc: Column<ColumnContext>) =>
                        cc.ctx.type === ColumnType.METRIC &&
                        cc.ctx.hasPillarHandle &&
                        cc.ctx.pillarExpanded,
                );
                c.children
                    .filter((cc) => cc.ctx.type === ColumnType.METRIC)
                    .forEach((cc, cci) => {
                        if (cci === 0) {
                            cc.ctx.hasPillarHandle = true;
                            cc.ctx.pillarExpanded = pillarExpanded;
                            return;
                        }
                        cc.ctx.hasPillarHandle = undefined;
                        cc.ctx.pillarExpanded = undefined;
                    });
            });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    makeColumns(
        viewPillar: ConfigViewPillar,
        viewMetric: ConfigViewMetric,
        supportedPillars: ConfigViewPillar[],
        supportedColumns: OptionalColumn[],
        metrics: Metric[],
        pillarsExpanded?: boolean,
        viewMetricExpanded?: boolean,
    ) {
        const atLeastOneSubPillar = supportedPillars.some(
            (e) => e.pillars && e.pillars.length !== 0,
        );

        this.columns = [
            {
                id: ColumnID.NODE.toString(),
                width: ColumnType_NODE_Width,
                // height: ColumnType_METRIC_Height * (atLeastOneSubPillar ? 3 : 2) + ColumnType_NODE_Height,
                compareFn: (
                    cellA: CellNode,
                    cellB: CellNode,
                    rowA: RowNode,
                    rowB: RowNode,
                    def: Sort,
                ) => this._compare(cellA, cellB, rowA, rowB, def),
                sticky: 'left',
                rowSpan: (atLeastOneSubPillar ? 3 : 2) + 1,
                ctx: {
                    type: ColumnType.NODE,
                },
                willMeasure: (node) => {
                    if (!node.renderContext || !node.renderContext.element)
                        return;
                    // node.renderContext.element.style.width = '';
                    node.renderContext.element.style.display = 'none';
                },
                didMeasure: (node) => {
                    if (!node.renderContext || !node.renderContext.element)
                        return;
                    // node.renderContext.element.style.width = '100%';
                    node.renderContext.element.style.display = 'block';
                },
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                measureElement: (node) =>
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    node.renderContext && node.renderContext.element,
            },
        ];

        let selPillar = supportedPillars.find(
            (e) => e.name === viewPillar.name,
        );
        let selSubPillar;
        if (!selPillar) {
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            selPillar = supportedPillars.find(
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                (e) => e.pillars.indexOf(viewPillar) !== -1,
            );
            selSubPillar = viewPillar;
        }
        const metric = viewMetric;

        this.metrics = [];
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const pushViewMetric = (vm) => {
            if (
                this.metrics.findIndex((e) => e.metricId === vm.metricId) !== -1
            )
                return;
            const m = metrics.find((e) => e.metricId === vm.metricId);
            m && this.metrics.push(m);
        };

        const makeMetricColumns = (
            pillar: ConfigViewPillar,
        ): Array<Column<ColumnContext>> => {
            if (!pillar.metrics || pillar.metrics.length === 0) {
                const pc = this.columnMaker(
                    `${ColumnType.METRIC}-STUB-undefined`,
                    null,
                    ColumnType.METRIC,
                    '-',
                    null,
                    undefined,
                    true,
                    false,
                    pillarsExpanded,
                    false,
                );

                // TODO revise. current actual always enabled
                const c = supportedColumns.find(
                    (e) => e.id === OptionalColumnType.CURRENT_ACTUAL,
                );
                pc.children = [
                    this.columnMaker(
                        `${c.id}-STUB-undefined`,
                        ColumnType_NODE_Width,
                        c.id as any as ColumnType,
                        c.name,
                        c.description,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        false,
                    ),
                ];

                return [pc];
            }

            const ordered = [...pillar.metrics];
            const index = ordered.findIndex(
                (e) => e.metricId === metric.metricId,
            );
            if (index !== -1) {
                const selected = ordered.splice(index, 1)[0];
                ordered.splice(0, 0, selected);
            }

            const columns = [];
            ordered.forEach((pm, i) => {
                // const pmComponents = pm.components && pm.components
                //     .filter(e => e.supportedColumns && e.supportedColumns.length !== 0); // TODO filter out in transformer
                let m: Metric;
                let pc: Column<ColumnContext>;
                let mc: OptionalColumn[];
                const metricExpanded =
                    viewMetricExpanded && viewMetric.metricId === pm.metricId;

                m = metrics.find((mm) => mm.metricId === pm.metricId);
                pc = this.columnMaker(
                    `${ColumnType.METRIC}-${pm.metricId}-undefined`,
                    null,
                    ColumnType.METRIC,
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    m && m.shortName,
                    null,
                    undefined,
                    i === 0,
                    pm.components && pm.components.length !== 0,
                    pillarsExpanded && i === 0,
                    metricExpanded,
                );
                columns.push(pc);

                // TODO revise. current actual always enabled
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                mc = supportedColumns.filter(
                    (c) =>
                        c.id === OptionalColumnType.CURRENT_ACTUAL ||
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        pm.supportedColumns.indexOf(c.id) !== -1,
                );
                pushViewMetric(pm);
                pc.children = mc.map((c) =>
                    this.columnMaker(
                        `${c.id}-${pm.metricId}-undefined`,
                        ColumnType_NODE_Width,
                        c.id as any as ColumnType,
                        c.name,
                        c.description,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        metricExpanded,
                    ),
                );

                const parentId = pc.id;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                pm.components &&
                    pm.components.forEach((cm) => {
                        m = metrics.find((mm) => mm.metricId === cm.metricId);
                        pc = this.columnMaker(
                            `${ColumnType.COMPONENT_METRIC}-${cm.metricId}-${pm.metricId}`,
                            null,
                            ColumnType.COMPONENT_METRIC,
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            m && m.shortName,
                            null,
                            parentId,
                            undefined,
                            undefined,
                            undefined,
                            metricExpanded,
                        );
                        columns.push(pc);

                        // TODO revise. current actual always enabled
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        mc = supportedColumns.filter(
                            (c) =>
                                c.id === OptionalColumnType.CURRENT_ACTUAL ||
                                // eslint-disable-next-line @typescript-eslint/prefer-includes
                                cm.supportedColumns.indexOf(c.id) !== -1,
                        );
                        pushViewMetric(cm);
                        pc.children = mc.map((c) =>
                            this.columnMaker(
                                `${c.id}-${cm.metricId}-${pm.metricId}`,
                                ColumnType_NODE_Width,
                                c.id as any as ColumnType,
                                c.name,
                                c.description,
                                undefined,
                                undefined,
                                undefined,
                                undefined,
                                metricExpanded,
                            ),
                        );
                    });
            });

            return columns;
        };

        const makeSubPillarColumns = (
            pillar: ConfigViewPillar,
            subPillars: ConfigViewPillar[],
        ): Array<Column<ColumnContext>> => {
            if (!subPillars || subPillars.length === 0) {
                const c = this.columnMaker(
                    `${ColumnType.SUB_PILLAR}-STUB-${pillar.name}`,
                    null,
                    ColumnType.SUB_PILLAR,
                    pillar.name,
                    null,
                    undefined,
                    undefined,
                    undefined,
                    pillarsExpanded,
                    undefined,
                );

                c.children = makeMetricColumns(pillar);

                return [c];
            }

            const ordered = [...subPillars];
            const index = !selSubPillar
                ? -1
                : ordered.findIndex((e) => e.name === selSubPillar.name);
            if (index !== -1) {
                const selected = ordered.splice(index, 1)[0];
                ordered.splice(0, 0, selected);
            }

            return ordered.map((e) => {
                const c = this.columnMaker(
                    `${ColumnType.SUB_PILLAR}-${e.name}-${pillar.name}`,
                    null,
                    ColumnType.SUB_PILLAR,
                    e.name,
                    null,
                    undefined,
                    undefined,
                    undefined,
                    pillarsExpanded,
                    undefined,
                );

                c.children = makeMetricColumns(e);

                return c;
            });
        };

        const makePillarColumns = (
            pillars: ConfigViewPillar[],
        ): Array<Column<ColumnContext>> => {
            const ordered = [...pillars];
            const index = ordered.findIndex((e) => e.name === selPillar.name);
            if (index !== -1) {
                const selected = ordered.splice(index, 1)[0];
                ordered.splice(0, 0, selected);
            }

            return ordered.map((e) => {
                const c = this.columnMaker(
                    `${ColumnType.PILLAR}-${e.name}-undefined`,
                    null,
                    ColumnType.PILLAR,
                    e.name,
                    null,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                );

                c.children = atLeastOneSubPillar
                    ? makeSubPillarColumns(e, e.pillars)
                    : makeMetricColumns(e);
                if (
                    atLeastOneSubPillar &&
                    (!e.pillars || e.pillars.length === 0)
                ) {
                    c.rowSpan = 2;
                }

                return c;
            });
        };

        this.columns.push(...makePillarColumns(supportedPillars));
        this.columnsFlatten = flattenArrayWithObj(this.columns, 'children');
    }

    private _makeDummyRow(
        parentLevelIdentifierPath: string,
        parentDataId: number,
        parentLevelDataIdPath: string,
        parentDepth: number,
    ): RowDefinition {
        return this.rowMaker(
            {
                id: NO_DATA_ID,
                name: TranslationKeyMap._711_NO_DATA_AVAILABLE,
                shortName: null,
                initials: null,
                dataId: Date.now() + Math.random(),
                metrics: padMissingMetricsWithMetrics(null, this.metrics),
                displayNameCriteriaIdType: null,
                outputCriteriaIdType: null,
                outputCriteriaIdTypes: null,
            },
            {
                identifier: null,
                performanceDataViewType: null,
                displayNameCriteriaIdType: null,
                outputCriteriaIdType: null,
                outputCriteriaIdTypes: null,
                inputCriteriaIdTypes: null,
                inputCriteriaIdTypeArgs: null,
                displayOrder: null,
                allowNewTab: null,
                newTabViewType: null,
                newTabPathType: null,
                previousLevelsRecurse: null,
                levels: null,
                name: null,
                shortName: null,
                initials: null,
            },
            parentLevelIdentifierPath,
            parentDepth,
            0,
            parentDataId,
            parentLevelDataIdPath,
        );
    }

    private _makeRows(
        level: ConfigPathLevel,
        parentLevelIdentifierPath: string,
        parentDataId: number,
        parentLevelDataIdPath: string,
    ): RowDefinition[] {
        if (!level) return null;
        const dat = this.data.find((e) => {
            const parentLevel = e.levelIdPath.substring(
                0,
                e.levelIdPath.lastIndexOf('|'),
            );
            return (
                e.levelId === level.identifier &&
                parentLevel === parentLevelIdentifierPath
            );
        });
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const peerGroupDat =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.peerGroupData &&
            this.peerGroupData.find((e) => {
                const parentLevel = e.levelIdPath.substring(
                    0,
                    e.levelIdPath.lastIndexOf('|'),
                );
                return (
                    e.levelId === level.identifier &&
                    parentLevel === parentLevelIdentifierPath
                );
            });
        if (!dat) return null;
        const rows = [];
        rows.push(
            ...dat.data
                .filter((e) => e.id !== NO_DATA_ID)
                .map((e, i) => {
                    const levelDataIdPath = [
                        parentLevelDataIdPath,
                        e.dataId,
                    ].join('|');
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    const peerGroupData =
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        peerGroupDat &&
                        peerGroupDat.data &&
                        peerGroupDat.data.length !== 0 &&
                        peerGroupDat.data[0];

                    const r = this.rowMaker(
                        e,
                        level,
                        dat.levelIdPath,
                        dat.depth,
                        i,
                        parentDataId,
                        levelDataIdPath,
                        peerGroupData,
                        dat.autoExpand,
                    );
                    r.ctx.hasNextLevel &&
                        level.levels.forEach((ee) => {
                            const childRows = this._makeRows(
                                ee,
                                dat.levelIdPath,
                                e.dataId,
                                levelDataIdPath,
                            );
                            if (childRows) {
                                if (!r.children) {
                                    r.children = [];
                                }
                                r.children.push(...childRows);
                                r.ctx.expanded = true;
                            }
                        });
                    return r;
                }),
        );

        const hasQueueLookupSuperGroup = this.data.some(
            (e) => e.queueLookupSuperGroup != null,
        );

        if (hasQueueLookupSuperGroup) {
            if (level.displayNameCriteriaIdType === CriteriaIdType.QUEUE_ID) {
                rows.sort(
                    (a, b) =>
                        (b.ctx?.hasSuperGroup ?? 0) -
                        (a.ctx?.hasSuperGroup ?? 0),
                );
            } else if (
                level.displayNameCriteriaIdType ===
                CriteriaIdType.SUPER_GROUP_ID
            ) {
                rows.sort(
                    (a, b) => (b.ctx?.hasQueue ?? 0) - (a.ctx?.hasQueue ?? 0),
                );
            }
        }
        return rows;
    }

    makeRows(rowId: string, levels: ConfigPathLevel[]): RowDefinition[] {
        if (!this.data) return [];

        const parsed = RowId.parse(rowId);

        let rows = levels.reduce((p, c) => {
            let ret = this._makeRows(
                c,
                parsed.levelIdentifierPath,
                parsed.dataId,
                parsed.parentLevelDataIdPath,
            );
            if (!ret || ret.length === 0) {
                ret = [
                    this._makeDummyRow(
                        parsed.levelIdentifierPath,
                        parsed.dataId,
                        parsed.parentLevelDataIdPath,
                        parsed.depth,
                    ),
                ];
            }
            p.push(...ret);
            return p;
        }, []);
        if (!rows || rows.length === 0) {
            rows = [
                this._makeDummyRow(
                    parsed.levelIdentifierPath,
                    parsed.dataId,
                    parsed.parentLevelDataIdPath,
                    parsed.depth,
                ),
            ];
        }
        return rows;
    }

    private _compare(
        cellA: CellNode,
        cellB: CellNode,
        rowA: RowNode,
        rowB: RowNode,
        def: Sort,
    ): number {
        try {
            const columnType = parseInt(def.columnId.split('-')[0]);
            const mode: 'number' | 'string' =
                columnType === ColumnType.NODE ? 'string' : 'number';
            const ctxA: MetricPair = cellA.context.ctx.value;
            const ctxB: MetricPair = cellB.context.ctx.value;
            let valA: any;
            let valB: any;
            switch (columnType) {
                case ColumnType.NODE:
                    valA = (ctxA as any as NodeInfo).name;
                    valB = (ctxB as any as NodeInfo).name;
                    break;
                case ColumnType.CURRENT_ACTUAL:
                    valA = ctxA.metric.actual;
                    valB = ctxB.metric.actual;
                    break;
                case ColumnType.PROJECTED_RESULT:
                    valA = ctxA.metric.projectedActual;
                    valB = ctxB.metric.projectedActual;
                    break;
                case ColumnType.PERIOD_TARGET:
                    valA = ctxA.metric.projectedTarget;
                    valB = ctxB.metric.projectedTarget;
                    break;
                case ColumnType.GAP:
                    valA = ctxA.metric.gap;
                    valB = ctxB.metric.gap;
                    break;
                case ColumnType.PROJECTED_PERCENT_TO_TARGET:
                    valA = ctxA.metric.projectedPctToTgt;
                    valB = ctxB.metric.projectedPctToTgt;
                    break;
                case ColumnType.PREVIOUS_ACTUAL:
                    valA = ctxA.metric.previousActual;
                    valB = ctxB.metric.previousActual;
                    break;
                case ColumnType.CURRENT_TARGET:
                    valA = ctxA.metric.target;
                    valB = ctxB.metric.target;
                    break;
                case ColumnType.GOAL:
                    valA = ctxA.metric.goal;
                    valB = ctxB.metric.goal;
                    break;
                case ColumnType.PERCENT_TO_TARGET:
                    valA = ctxA.metric.percentToTarget;
                    valB = ctxB.metric.percentToTarget;
                    break;
                case ColumnType.PEER_GROUP_ACTUAL:
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    valA = ctxA.peerGroup && ctxA.peerGroup.actual;
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    valB = ctxB.peerGroup && ctxB.peerGroup.actual;
                    break;
                case ColumnType.PEER_GROUP_PERCENT_TO_TARGET:
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    valA = ctxA.peerGroup && ctxA.peerGroup.percentToTarget;
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    valB = ctxB.peerGroup && ctxB.peerGroup.percentToTarget;
                    break;
                case ColumnType.OBJECTIVE_MET:
                    valA = ctxA.metric.objectiveMet;
                    valB = ctxB.metric.objectiveMet;
                    break;
                case ColumnType.WEIGHT:
                    valA = ctxA.metric.weight;
                    valB = ctxB.metric.weight;
                    break;
                case ColumnType.WEIGHTED_SCORE:
                    valA = ctxA.metric.weightedScore;
                    valB = ctxB.metric.weightedScore;
                    break;
                default:
                    console.log(new Error('invalid column'));
                    return Number.MIN_SAFE_INTEGER;
            }
            if (mode === 'number') {
                valA = valA == null ? Number.MIN_SAFE_INTEGER : valA;
                valB = valB == null ? Number.MIN_SAFE_INTEGER : valB;
            } else {
                valA = valA == null ? '' : valA;
                valB = valB == null ? '' : valB;
            }
            return mode === 'number'
                ? def.order === SortOrder.ASC
                    ? valA - valB
                    : valB - valA
                : def.order === SortOrder.ASC
                ? valA.localeCompare(valB)
                : valB.localeCompare(valA);
        } catch (e) {
            console.log(e);
            return Number.MIN_SAFE_INTEGER;
        }
    }

    processInputCriteriaIdTypes(row?: RowNode): LevelDataMap[] {
        const levels = !row
            ? this.path.levels
            : findLevel(
                  this.path.levels,
                  row.context.ctx.levelDataMap.identifier,
              ).levels;
        const parentLevelsDataMap = concatLevelDataMap(row);
        const levelsDataMap: LevelDataMap[] = [];

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this.didProcessInputCriteriaIdTypes &&
            this.didProcessInputCriteriaIdTypes(
                levels,
                parentLevelsDataMap,
                levelsDataMap,
            );

        return levelsDataMap;
    }

    columnMaker = (
        id: string,
        width: number,
        type: ColumnType,
        name: string,
        description: string,
        parentId: string,
        hasPillarHandle: boolean,
        hasComponents: boolean,
        pillarExpanded: boolean,
        metricExpanded: boolean,
    ): Column<ColumnContext> => ({
        id,
        width,
        height:
            type === ColumnType.PILLAR ||
            type === ColumnType.SUB_PILLAR ||
            type === ColumnType.METRIC ||
            type === ColumnType.COMPONENT_METRIC
                ? ColumnType_METRIC_Height
                : ColumnType_NODE_Height,
        compareFn:
            type !== ColumnType.METRIC &&
            type !== ColumnType.COMPONENT_METRIC &&
            ((
                cellA: CellNode,
                cellB: CellNode,
                rowA: RowNode,
                rowB: RowNode,
                def: Sort,
            ) => this._compare(cellA, cellB, rowA, rowB, def)),
        canBeDroppedOnFn: (drag: CellNode, drop: CellNode) => {
            if (drag.context.c.ctx.type === ColumnType.METRIC) {
                return drop.context.c.ctx.type === ColumnType.METRIC;
            }
            if (drag.context.c.ctx.type === ColumnType.COMPONENT_METRIC) {
                return (
                    drop.context.c.ctx.parentId === drag.context.c.ctx.parentId
                );
            }
            return false;
        },
        ctx: {
            tpl: this.columnTpl,
            type,
            name,
            description,
            parentId,
            hasPillarHandle,
            hasComponents,
            pillarExpanded,
            metricExpanded,
        },
        willMeasure: (node) => {
            if (!node.renderContext || !node.renderContext.element) return;
            node.renderContext.element.classList.add('w-auto');
        },
        didMeasure: (node) => {
            if (!node.renderContext || !node.renderContext.element) return;
            node.renderContext.element.classList.remove('w-auto');
        },
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        measureElement: (node) =>
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            node.renderContext && node.renderContext.element,
    });

    private _validateKey(
        data: MetricData,
        level: ConfigPathLevel,
        parentDataId: number,
    ): [boolean, boolean] {
        const userId =
            this.hierarchyUserInfoCurrent &&
            this.hierarchyUserInfoCurrent.length > 0
                ? parentDataId
                : null;
        const superGroupId =
            userId != null
                ? this.hierarchyUserInfoCurrent?.find(
                      (e) => e.userId === userId,
                  )?.superGroupId
                : parentDataId;

        if (
            level.performanceDataViewType !==
                PerformanceDataViewType.SUPER_GROUP_QUEUE &&
            level.performanceDataViewType !== PerformanceDataViewType.USER_QUEUE
        ) {
            return [null, null];
        }

        const key =
            level.displayNameCriteriaIdType === CriteriaIdType.SUPER_GROUP_ID
                ? `${parentDataId}-${data.id}-${userId}`
                : `${data.id}-${superGroupId}-${userId}`;

        const queueLookupSuperGroup = this.data.find(
            (e) => e.queueLookupSuperGroup != null,
        )?.queueLookupSuperGroup;
        const hasKey = queueLookupSuperGroup?.[key] === true;

        if (level.displayNameCriteriaIdType === CriteriaIdType.SUPER_GROUP_ID) {
            return [null, hasKey];
        } else {
            return [hasKey, null];
        }
    }

    rowMaker = (
        data: MetricData,
        level: ConfigPathLevel,
        levelIdentifierPath: string,
        depth: number,
        index: number,
        parentDataId: number,
        parentLevelDataIdPath: string,
        peerGroupData?: MetricData,
        autoExpand?: number[],
    ): RowDefinition => {
        const isUser =
            level.displayNameCriteriaIdType === CriteriaIdType.USER_ID ||
            level.displayNameCriteriaIdType === CriteriaIdType.TEAM_ID;
        const userInfo =
            isUser &&
            this.hierarchyUserInfo &&
            this.hierarchyUserInfo.find((e) => e.userId === data.id);
        const userInfoCurrent =
            isUser &&
            this.hierarchyUserInfoCurrent &&
            this.hierarchyUserInfoCurrent.find((e) => e.userId === data.id);
        const levelDataMapRole = [];
        const [hasSuperGroup, hasQueue] = this._validateKey(
            data,
            level,
            parentDataId,
        );

        const row: RowDefinition = {
            ctx: {
                levelDataMap: {
                    // TODO change to array? merge levelDataMapRole?
                    identifier: level.identifier,
                    performanceDataViewType: level.performanceDataViewType,
                    displayNameCriteriaIdType: level.displayNameCriteriaIdType,
                    outputCriteriaType: level.outputCriteriaIdType,
                    dataId: data.dataId,
                    outputCriteriaTypes: [level.outputCriteriaIdType],
                    outputCriteriaTypeArgs: {
                        [level.outputCriteriaIdType]: data.dataId,
                    },
                },
                levelDataMapRole,
                depth,
                hasNextLevel: level.levels && level.levels.length > 0,
                allowNewTab: level.allowNewTab,
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                autoExpand: autoExpand && autoExpand.some((e) => e === data.id),
                hasSuperGroup,
                hasQueue,
            },
            id: RowId.create(
                level.identifier,
                levelIdentifierPath,
                depth,
                index,
                data.dataId,
                parentDataId,
                parentLevelDataIdPath,
            ),
            height: ColumnType_NODE_Height,
            cells: this.columnsFlatten
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                .filter(
                    (c) =>
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        [
                            ColumnType.PILLAR,
                            ColumnType.SUB_PILLAR,
                            ColumnType.METRIC,
                            ColumnType.COMPONENT_METRIC,
                        ].indexOf(c.ctx.type) === -1,
                )
                .map((c) => {
                    if (c.id === ColumnID.NODE.toString()) {
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        return {
                            ctx: {
                                tpl: this.nodeTpl,
                                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                value: {
                                    name:
                                        data.name ||
                                        (level.performanceDataViewType ===
                                        PerformanceDataViewType.MARKET
                                            ? TranslationKeyMap._832_MARKET
                                            : null),
                                    unSelectable:
                                        data.id === NO_DATA_ID ||
                                        data.displayNameCriteriaIdType ===
                                            CriteriaIdType.QUEUE_ID ||
                                        data.displayNameCriteriaIdType ===
                                            CriteriaIdType.QUEUE_ID_2 /* TODO revise */,
                                    notCurrentManager:
                                        userInfo &&
                                        userInfoCurrent &&
                                        userInfo.managerId !==
                                            userInfoCurrent.managerId,
                                } as NodeInfo,
                            },
                            columnId: c.id,
                            willMeasure: (node) => {
                                if (
                                    !node.renderContext ||
                                    !node.renderContext.element ||
                                    node.renderContext.element.children
                                        .length === 0
                                )
                                    return;
                                node.renderContext.element.children[0].classList.remove(
                                    'w-100',
                                );
                                // node.renderContext.element.children[0].classList.add('w-auto');
                            },
                            didMeasure: (node) => {
                                if (
                                    !node.renderContext ||
                                    !node.renderContext.element ||
                                    node.renderContext.element.children
                                        .length === 0
                                )
                                    return;
                                // node.renderContext.element.children[0].classList.remove('w-auto');
                                node.renderContext.element.children[0].classList.add(
                                    'w-100',
                                );
                            },
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            measureElement: (node) =>
                                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                node.renderContext &&
                                node.renderContext.element &&
                                node.renderContext.element.children.length !==
                                    0 &&
                                node.renderContext.element.children[0],
                        } as Cell;
                    }
                    const metricId = parseInt(c.id.split('-')[1]);
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    return {
                        ctx: {
                            tpl: this.metricTpl,
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                            value: {
                                metric:
                                    data.metrics.find(
                                        (e) => e.id === metricId,
                                    ) ||
                                    {} /* TODO config metrics aren't padded */,
                                peerGroup: !peerGroupData
                                    ? null
                                    : peerGroupData.metrics.find(
                                          (ee) => ee.id === metricId,
                                      ),
                            } as MetricPair,
                        },
                        columnId: c.id,
                        willMeasure: (node) => {
                            if (
                                !node.renderContext ||
                                !node.renderContext.element ||
                                node.renderContext.element.children.length === 0
                            )
                                return;
                            node.renderContext.element.children[0].classList.add(
                                'w-auto',
                            );
                        },
                        didMeasure: (node) => {
                            if (
                                !node.renderContext ||
                                !node.renderContext.element ||
                                node.renderContext.element.children.length === 0
                            )
                                return;
                            node.renderContext.element.children[0].classList.remove(
                                'w-auto',
                            );
                        },
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        measureElement: (node) =>
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            node.renderContext &&
                            node.renderContext.element &&
                            node.renderContext.element.children.length !== 0 &&
                            node.renderContext.element.children[0],
                    } as Cell;
                }),
        };

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this.didMakeRow && this.didMakeRow(level, userInfo, levelDataMapRole);

        return row;
    };
}
